import { forwardRef } from "react";

export const StageBadge = ({ status, children }) => {
  const map = {
    received: "status-info", pending: "status-warning", available: "status-success",
    cut: "status-info", printing: "status-warning", ready: "status-success",
    sewing: "status-warning", qc_done: "status-info", in_stock: "status-success",
    dispatched: "status-neutral", partial_dispatch: "status-warning",
    completed: "status-success", partial: "status-warning", sent: "status-info",
    rejected: "status-destructive", created: "status-neutral",
    to_be_ordered: "status-warning", ordered: "status-info", not_required: "status-neutral",
    to_be_sourced: "status-warning",
  };
  const cls = map[status] || "status-neutral";
  return <span className={`stage-badge ${cls}`}>{children || status}</span>;
};

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 border-b border-zinc-200 bg-white px-4 sm:px-6 lg:px-8 py-4 sm:py-5" data-testid={testid}>
    <div>
      <div className="data-label">{subtitle}</div>
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-zinc-950 mt-1">{title}</h1>
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const BtnPrimary = ({ children, className = "", ...rest }) => (
  <button
    className={`bg-zinc-950 text-white rounded-sm font-medium hover:bg-zinc-800 active:scale-[0.98] transition-all px-4 py-2 text-sm ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const BtnSecondary = ({ children, className = "", ...rest }) => (
  <button
    className={`bg-white text-zinc-900 border border-zinc-200 rounded-sm font-medium hover:bg-zinc-100 transition-all px-4 py-2 text-sm ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const BtnDanger = ({ children, className = "", ...rest }) => (
  <button
    className={`bg-red-600 text-white border border-red-600 rounded-sm font-medium hover:bg-red-700 transition-all px-4 py-2 text-sm ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const Input = forwardRef(function Input(props, ref) {
  // For type="number" inputs: block mouse-wheel from changing the value AND
  // stop arrow-up / arrow-down from nudging the number when the user meant to
  // scroll the page or move the caret. This prevents accidental mis-entries
  // (very common while filling cut qty / weights on laptop trackpads).
  const isNumber = props.type === "number";
  const handleWheel = (e) => {
    if (e.target && typeof e.target.blur === "function") e.target.blur();
  };
  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
    if (props.onKeyDown) props.onKeyDown(e);
  };
  return (
    <input
      ref={ref}
      {...props}
      onWheel={isNumber ? handleWheel : props.onWheel}
      onKeyDown={isNumber ? handleKeyDown : props.onKeyDown}
      className={`bg-white border border-zinc-300 rounded-sm focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 outline-none h-10 px-3 py-2 text-sm text-zinc-900 w-full ${props.className || ""}`}
    />
  );
});

export const Textarea = (props) => (
  <textarea
    {...props}
    className={`bg-white border border-zinc-300 rounded-sm focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 outline-none px-3 py-2 text-sm text-zinc-900 w-full ${props.className || ""}`}
  />
);

export const Select = (props) => (
  <select
    {...props}
    className={`bg-white border border-zinc-300 rounded-sm focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 outline-none h-10 px-3 py-2 text-sm text-zinc-900 w-full ${props.className || ""}`}
  >
    {props.children}
  </select>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white border border-zinc-200 rounded-sm ${className}`} {...rest}>{children}</div>
);

/*
 Table supports an optional `totals` array (one entry per header column).
 Pass null/undefined/"" to skip a column in the totals row.
*/
export const Table = ({ headers, children, testid, totals }) => (
  <div className="border border-zinc-200 overflow-x-auto bg-white rounded-sm">
    <table className="w-full min-w-[640px]" data-testid={testid}>
      <thead>
        <tr className="bg-zinc-50 border-b border-zinc-200">
          {headers.map((h, i) => (
            <th key={i} className="text-left py-3 px-4 text-[11px] font-semibold text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
      {totals && totals.length > 0 && (
        <tfoot>
          <tr className="bg-zinc-50 border-t-2 border-zinc-300" data-testid={testid ? `${testid}-totals` : undefined}>
            {totals.map((v, i) => (
              <td key={i} className="py-3 px-4 text-sm font-mono font-semibold text-zinc-950 whitespace-nowrap">
                {v ?? ""}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

export const Td = ({ children, className = "", mono = false }) => (
  <td className={`py-3 px-4 text-sm text-zinc-900 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
);

export const Tr = ({ children }) => (
  <tr className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">{children}</tr>
);

export const Label = ({ children }) => (
  <label className="data-label block mb-1.5">{children}</label>
);

export const Modal = ({ open, onClose, title, children, testid, size = "sm" }) => {
  if (!open) return null;
  // Responsive sizes — width adapts on tablet/phone, expands on desktop
  const sizeClass = {
    sm: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
    xxl: "max-w-[1500px]",
    full: "max-w-[1400px]",
  }[size] || "max-w-2xl";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid={testid}>
      <div className={`bg-white border border-zinc-200 rounded-sm ${sizeClass} w-full max-h-[94vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div className="font-heading text-lg sm:text-xl font-bold text-zinc-950 tracking-tight">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950 text-2xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, hint }) => (
  <div className="text-center py-16 border border-dashed border-zinc-300 bg-zinc-50/50 rounded-sm">
    {Icon && <Icon size={32} className="mx-auto text-zinc-400 mb-3" strokeWidth={1.5} />}
    <div className="font-heading text-lg font-bold text-zinc-800">{title}</div>
    {hint && <div className="text-sm text-zinc-500 mt-1">{hint}</div>}
  </div>
);

/* Helper to compute column sums from an array of row objects */
export const sum = (arr, key) => (arr || []).reduce((a, b) => a + Number(b?.[key] || 0), 0);
